import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Lock, Mail } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back!");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center p-4 sm:p-6"
         style={{
           backgroundImage:
             "linear-gradient(135deg, rgba(6,11,20,0.92), rgba(6,11,20,0.98)), url('https://images.unsplash.com/photo-1632988954597-e5a89837ed5b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxnb2xkJTIwc3BhcmtsZXMlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8YmxhY2t8MTc4NDgyNzQzMXww&ixlib=rb-4.1.0&q=85')",
           backgroundSize: "cover", backgroundPosition: "center",
         }}>
      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-8 items-center">
        {/* Left branding */}
        <div className="hidden lg:flex flex-col justify-between h-full py-8 pr-6">
          <div className="flex items-center gap-3">
            <img src="/hos-logo.webp" alt="House of Spells" className="h-12 w-12 object-contain" />
            <div className="leading-tight">
              <div className="font-heading text-2xl">House of Spells</div>
              <div className="label-caps text-[10px]">Times Square · New York</div>
            </div>
          </div>

          <div className="my-10 flex flex-col items-start">
            <img src="/hos-logo.webp" alt="" className="h-40 w-40 object-contain mb-6 opacity-90" />
            <div className="label-caps mb-3">Store Operations</div>
            <h1 className="font-heading text-5xl leading-[1.05] mb-5">
              Open &amp; close,<br/>the magical way.
            </h1>
            <p className="text-[color:var(--text-secondary)] max-w-md leading-relaxed">
              Digital opening and closing checklists for the House of Spells Times Square flagship —
              fast, auditable, and mobile-friendly.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-3 text-center">
            <MiniStat k="Opening" v="26 tasks" />
            <MiniStat k="Closing" v="28 tasks" />
            <MiniStat k="Audit" v="Signed" />
          </div>
        </div>

        {/* Right form card */}
        <div className="w-full max-w-md mx-auto lg:mx-0">
          <div className="hos-card p-6 sm:p-8">
            <div className="mb-6 lg:hidden flex items-center gap-2">
              <img src="/hos-logo.webp" alt="House of Spells" className="h-9 w-9 object-contain"/>
              <span className="font-heading text-2xl">House of Spells</span>
            </div>
            <div className="label-caps mb-1">Sign in</div>
            <h2 className="font-heading text-3xl sm:text-4xl mb-6">Welcome back, sorcerer.</h2>

            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div>
                <label className="text-[11px] uppercase tracking-widest text-[color:var(--text-secondary)]">Email</label>
                <div className="relative mt-1">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-secondary)]"/>
                  <input
                    data-testid="login-email-input"
                    type="email"
                    className="hos-input pl-10"
                    placeholder="you@houseofspells.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-[color:var(--text-secondary)]">Password</label>
                <div className="relative mt-1">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-secondary)]"/>
                  <input
                    data-testid="login-password-input"
                    type="password"
                    className="hos-input pl-10"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              </div>
              <button
                data-testid="login-submit-btn"
                disabled={busy}
                type="submit"
                className="hos-btn-primary w-full mt-2"
              >
                {busy ? "Signing in…" : "Enter the store"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ k, v }) {
  return (
    <div className="hos-card p-3">
      <div className="label-caps text-[10px]">{k}</div>
      <div className="font-heading text-lg text-[color:var(--gold)]">{v}</div>
    </div>
  );
}
